import React from "react";
import styled from "styled-components";

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 6rem 2rem 4rem;
  background: ${(props) => props.theme.colors.backgroundLight};
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: white;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: ${(props) => props.theme.shadows.medium};

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${(props) => props.theme.colors.primary};
  font-family: ${(props) => props.theme.fonts.heading};
  margin-bottom: 1rem;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LastUpdated = styled.p`
  text-align: center;
  color: ${(props) => props.theme.colors.textLight};
  font-size: 0.9rem;
  margin-bottom: 2rem;
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: ${(props) => props.theme.colors.textWarm};
  font-family: ${(props) => props.theme.fonts.heading};
  margin-bottom: 1rem;
  margin-top: 1.5rem;
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.8;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: 1rem;
`;

const List = styled.ul`
  margin-right: 2rem;
  margin-bottom: 1rem;
`;

const ListItem = styled.li`
  font-size: 1rem;
  line-height: 1.8;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: 0.5rem;
`;

const ContactBox = styled.div`
  background: ${(props) => props.theme.colors.backgroundLight};
  padding: 1.5rem;
  border-radius: 8px;
  border-right: 4px solid ${(props) => props.theme.colors.primary};
  margin: 1.5rem 0;
`;

const ContactTitle = styled.h3`
  color: ${(props) => props.theme.colors.textWarm};
  margin-bottom: 0.75rem;
  font-size: 1.2rem;
`;

const ContactInfo = styled.p`
  font-size: 1rem;
  line-height: 1.8;
  margin-bottom: 0.5rem;
`;

const AccessibilityStatement: React.FC = () => {
  return (
    <PageContainer>
      <ContentWrapper>
        <Title>הצהרת נגישות</Title>
        <LastUpdated>עדכון אחרון: נובמבר 2024</LastUpdated>

        <Section>
          <SectionTitle>מחויבות לנגישות</SectionTitle>
          <Paragraph>
            לי פרוינד - פסיכולוגית חינוכית מחויבת להנגשת אתר האינטרנט שלה לכל
            אוכלוסיית המשתמשים, כולל אנשים עם מוגבלויות. אנו מאמינים שכל אדם
            זכאי לגלוש באינטרנט בחופשיות, בנוחות ובשוויון, ולכן עושים את מירב
            המאמצים להנגיש את האתר לאנשים עם מוגבלויות, בהתאם לתקנות שוויון
            זכויות לאנשים עם מוגבלות, תשע"ג-2013.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>תקנים ויישום</SectionTitle>
          <Paragraph>
            האתר מונגש בהתאם לתקן הישראלי (ת"י 5568) ברמת AA, ובהתאם להמלצות
            התוכן הנגיש לאינטרנט (WCAG 2.0) ברמה AA. ההנגשה מאפשרת שימוש בדפדפנים
            מובילים ובטכנולוגיות מסייעות כמו תוכנות קוראות מסך.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>מאפייני נגישות באתר</SectionTitle>
          <Paragraph>
            במסגרת מאמץ ההנגשה, האתר כולל את התכונות והפתרונות הבאים:
          </Paragraph>
          <List>
            <ListItem>
              <strong>ניווט מקלדת מלא:</strong> ניתן לנווט באתר באמצעות מקלדת בלבד,
              ללא צורך בעכבר. כולל אינדיקטורים ויזואליים ברורים למיקוד (Focus Indicators)
            </ListItem>
            <ListItem>
              <strong>קישור "דלג לתוכן הראשי":</strong> קישור נגישות המופיע בתחילת
              הדף ומאפשר למשתמשי מקלדת וקוראי מסך לדלג ישירות לתוכן הראשי
            </ListItem>
            <ListItem>
              <strong>תמיכה בקוראי מסך:</strong> האתר כולל תיוג ARIA מלא ותומך
              בתוכנות קריאת מסך המובילות כגון JAWS, NVDA ו-VoiceOver
            </ListItem>
            <ListItem>
              <strong>מבנה סמנטי:</strong> שימוש נכון בתגיות HTML5 סמנטיות וכותרות
              מובנות (H1-H6) לצורך ניווט קל וברור
            </ListItem>
            <ListItem>
              <strong>טקסט חלופי מפורט:</strong> כל התמונות באתר כוללות תיאור
              טקסטואלי מפורט (Alt text) המתאר את תוכן התמונה
            </ListItem>
            <ListItem>
              <strong>ניגודיות צבעים:</strong> שמירה על ניגודיות מינימלית של 4.5:1
              בין צבעי הטקסט והרקע בהתאם לדרישות WCAG 2.0 AA
            </ListItem>
            <ListItem>
              <strong>טפסים נגישים:</strong> כל שדות הטופס כוללים תוויות (Labels)
              ברורות, תיוג ARIA, והודעות שגיאה נגישות לקוראי מסך
            </ListItem>
            <ListItem>
              <strong>קישורים תיאוריים:</strong> כל הקישורים באתר כוללים תיאור
              ברור ליעדם באמצעות aria-label
            </ListItem>
            <ListItem>
              <strong>עיצוב רספונסיבי:</strong> האתר מותאם לצפייה במגוון מכשירים
              וגדלי מסך
            </ListItem>
            <ListItem>
              <strong>תמיכה בהעדפות משתמש:</strong> האתר מכבד הגדרות מערכת כמו
              "prefers-reduced-motion" למשתמשים הרגישים לתנועה
            </ListItem>
            <ListItem>
              <strong>אזורי ARIA Landmarks:</strong> שימוש נכון ב-role attributes כמו
              banner, main, navigation, contentinfo לניווט קל יותר
            </ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>דפדפנים נתמכים</SectionTitle>
          <Paragraph>האתר נבדק ומותאם לעבודה עם הדפדפנים הבאים:</Paragraph>
          <List>
            <ListItem>Google Chrome (גרסאות עדכניות)</ListItem>
            <ListItem>Mozilla Firefox (גרסאות עדכניות)</ListItem>
            <ListItem>Safari (גרסאות עדכניות)</ListItem>
            <ListItem>Microsoft Edge (גרסאות עדכניות)</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>טכנולוגיות מסייעות</SectionTitle>
          <Paragraph>האתר תומך בטכנולוגיות המסייעות הבאות:</Paragraph>
          <List>
            <ListItem>קוראי מסך (JAWS, NVDA, VoiceOver, TalkBack)</ListItem>
            <ListItem>תוכנות הגדלת מסך</ListItem>
            <ListItem>תוכנות להקראת טקסט</ListItem>
            <ListItem>ניווט מקלדת בלבד</ListItem>
            <ListItem>מערכות הפעלה המאפשרות העדפות נגישות</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>תהליך ההנגשה</SectionTitle>
          <Paragraph>
            האתר עבר תהליך הנגשה מקיף הכולל:
          </Paragraph>
          <List>
            <ListItem>
              בדיקה ועמידה בהנחיות הנגישות הישראליות (תקן ת"י 5568) והבינלאומיות
              (WCAG 2.0 Level AA)
            </ListItem>
            <ListItem>
              הוספת תיוג ARIA מלא לכל רכיבי האתר
            </ListItem>
            <ListItem>
              בדיקות ידניות לזיהוי בעיות נגישות
            </ListItem>
            <ListItem>
              בדיקת ניווט מקלדת בכל דפי האתר
            </ListItem>
            <ListItem>
              בדיקת תאימות לקוראי מסך
            </ListItem>
            <ListItem>
              תיקון ושיפור מתמשך של בעיות נגישות שזוהו
            </ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>מגבלות נגישות ידועות</SectionTitle>
          <Paragraph>
            האתר מונגש בהתאם לתקן הישראלי ת"י 5568 ברמת AA. ייתכן שחלק
            מהתכנים החיצוניים (כגון סרטונים מטמעים או תוכן צד שלישי) אינם תחת
            שליטתנו המלאה ועשויים שלא לעמוד במלואם בדרישות הנגישות. אנו עובדים
            באופן שוטף לשיפור הנגישות ולפתרון בעיות שמתגלות.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>משוב ופניות</SectionTitle>
          <Paragraph>
            אנו שמחים לקבל משוב על נגישות האתר. אם נתקלת בבעיות נגישות או אם יש
            לך הצעות לשיפור, אנא פנה אלינו ונעשה כמיטב יכולתנו לסייע ולתקן את
            הבעיה במהירות האפשרית.
          </Paragraph>

          <ContactBox>
            <ContactTitle>ליצירת קשר בנושא נגישות:</ContactTitle>
            <ContactInfo>
              <strong>רכזת נגישות:</strong> לי פרוינד
            </ContactInfo>
            <ContactInfo>
              <strong>טלפון:</strong> 054-5954754
            </ContactInfo>
            <ContactInfo>
              <strong>דוא"ל:</strong> leefreund90@gmail.com
            </ContactInfo>
            <ContactInfo>
              <strong>זמן תגובה:</strong> נשתדל לחזור אליך תוך 5 ימי עסקים
            </ContactInfo>
          </ContactBox>

          <Paragraph>
            בפנייתך אנא ציין את שמך המלא, פרטי קשר, תיאור הבעיה שנתקלת בה,
            והדפדפן והמכשיר בהם השתמשת.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>סדרי פניה לממונה על הנגישות במשרד המשפטים</SectionTitle>
          <Paragraph>
            בהתאם לתקנות, ניתן לפנות בכל עת לממונה על הנגישות במשרד המשפטים:
          </Paragraph>
          <ContactInfo>
            <strong>כתובת:</strong> רחוב צלאח א-דין 34 (מתחם הקריה המזרחית),
            ירושלים
          </ContactInfo>
          <ContactInfo>
            <strong>טלפון:</strong> 02-6467011
          </ContactInfo>
          <ContactInfo>
            <strong>דוא"ל:</strong> <a href="mailto:accessibility@justice.gov.il">accessibility@justice.gov.il</a>
          </ContactInfo>
        </Section>

        <Section>
          <SectionTitle>עדכונים והתאמות</SectionTitle>
          <Paragraph>
            אנו מחויבים לשמור על רמת הנגישות באתר לאורך זמן. במסגרת זו, נבצע
            בדיקות תקופתיות ועדכונים שוטפים כדי להבטיח שהאתר עומד בדרישות
            הנגישות המחמירות ביותר ומתאים לצרכים המשתנים של המשתמשים.
          </Paragraph>
        </Section>

        <Section>
          <Paragraph>
            הצהרת נגישות זו עודכנה לאחרונה בנובמבר 2024. אנו שומרים לעצמנו את
            הזכות לעדכן הצהרה זו מעת לעת.
          </Paragraph>
        </Section>
      </ContentWrapper>
    </PageContainer>
  );
};

export default AccessibilityStatement;
